import {
    ApiShopperLandingPageLink,
    ApiPromotion,
} from '@storefronts/common-types'

/**
 * Calculate discount percentage between original and final price
 * Returns 0 if no discount or invalid inputs
 */
export const calculateDiscountPercentage = (
    originalPrice: number,
    finalPrice: number,
): number => {
    if (!originalPrice || originalPrice <= 0 || originalPrice <= finalPrice) {
        return 0
    }
    return Math.floor(((originalPrice - finalPrice) / originalPrice) * 100)
}

/** Whether the promotion came from a synced Shopify discount. */
export const hasShopifyId = (promotion: ApiPromotion): boolean =>
    promotion.shopifyId != null && promotion.shopifyId !== ''

/**
 * Shopify stacks a page discount with a product discount only when both opt
 * in: the page discount must combine with product discounts, and the product
 * discount must combine with the page discount's class.
 */
export const pageDiscountStacksWith = (
    pageDiscount: ApiPromotion,
    productDiscount: ApiPromotion,
): boolean =>
    pageDiscount.combinesWithProductDiscounts &&
    (pageDiscount.isOrderLevel
        ? productDiscount.combinesWithOrderDiscounts
        : productDiscount.combinesWithProductDiscounts)

// Returns a discount's value as if it's a fixed amount in order to compare fixed and percent based discounts
// (basePrice is used to determine the dollar value of the discount)
export const getDiscountValue = (
    discount: ApiPromotion,
    basePrice: number,
): number => {
    if (discount.discountType.toLowerCase() === 'fixed') {
        return discount.discountValue
    }
    return basePrice * (discount.discountValue / 100)
}

// Compares two discounts to determine which is the best using the same behaviour as sorting compareFn()
// returns of positive value means discount1 is larger, 0 means equal and negative means discount2 is larger
// Percentage discounts are preferred and for now automatically determined to be larger than fixed discounts.
// NOTE: If we're sticking with that, maybe the baseprice is not really required since we are not
// comparing the actual discount values between fixed and percent anyway.
const compareDiscounts = (
    discount1: ApiPromotion | null,
    discount2: ApiPromotion,
    basePrice: number,
): number => {
    if (discount1 === null) {
        return -1
    }
    // If both discounts are of the same type, then return the difference between them.
    if (
        discount1.discountType.toLowerCase() ===
        discount2.discountType.toLowerCase()
    ) {
        return (
            getDiscountValue(discount1, basePrice) -
            getDiscountValue(discount2, basePrice)
        )
    }
    // Otherwise, return the one that is percentage
    return discount1.discountType.toLowerCase() === 'percent' ? 1 : -1
}

const selectLargestDiscount = (
    discounts: ApiPromotion[],
    basePrice: number,
): ApiPromotion | null => {
    if (discounts.length === 0) return null
    let bestDiscount: ApiPromotion | null = null
    for (const discount of discounts) {
        if (compareDiscounts(bestDiscount, discount, basePrice) < 0) {
            bestDiscount = discount
        }
    }
    return bestDiscount
}

const PRODUCT_GID_PREFIX = 'gid://shopify/Product/'

/**
 * Whether a promotion may be displayed on a specific product, based on the
 * product scope resolved from the synced Shopify discount.
 * Unscoped promotions (no productScope) apply to every product.
 * eligibleShopifyProductIds holds raw ids; the passed id may be raw or a GID.
 */
export function promotionAppliesToProduct(
    promotion: ApiPromotion,
    shopifyProductId: string | null | undefined,
): boolean {
    switch (promotion.productScope) {
        case 'NONE':
            return false
        case 'SPECIFIC': {
            if (!shopifyProductId) return false
            const rawId = shopifyProductId.replace(PRODUCT_GID_PREFIX, '')
            return (promotion.eligibleShopifyProductIds ?? []).includes(rawId)
        }
        default:
            return true
    }
}

/**
 * Returns true if two promotions refer to the same discount.
 * Compare by shopifyId when both have it; by id when neither has shopifyId; no match when one has shopifyId and the other doesn't.
 */
export function promotionsMatch(a: ApiPromotion, b: ApiPromotion): boolean {
    const aHasShopifyId = hasShopifyId(a)
    const bHasShopifyId = hasShopifyId(b)
    if (aHasShopifyId && bHasShopifyId) return a.shopifyId === b.shopifyId
    if (!aHasShopifyId && !bHasShopifyId) return a.id === b.id
    return false
}

/**
 * Returns the best possible combination of discounts for a given product
 * taking into consideration the product's own discount and the page's set discount
 * and their combinability
 * @param productLink the product's LandingPageLInk with productDiscounts
 * @param pageDiscount the page's own discount
 * @param basePrice (optional) the selected variant's base price
 * @returns the array of acceptable discounts
 */
export const getBestDiscountsForProducts = (
    productLink: ApiShopperLandingPageLink,
    pageDiscount: ApiPromotion | null,
    basePrice?: number,
): ApiPromotion[] | null => {
    // If basePrice is already 0 (or negative) return no discounts
    if (basePrice !== undefined && basePrice <= 0) {
        return null
    }

    // A page discount scoped away from this product behaves as no page discount
    const applicablePageDiscount =
        pageDiscount !== null &&
        promotionAppliesToProduct(pageDiscount, productLink.shopifyId)
            ? pageDiscount
            : null

    const productDiscounts = productLink.productDiscounts
    // There are no product discounts return the pageDiscount
    if (!productDiscounts || productDiscounts.length === 0) {
        return applicablePageDiscount === null
            ? applicablePageDiscount
            : [applicablePageDiscount]
    }
    const price = basePrice ?? productLink.price.price

    // When there are no pageDiscounts, return the largest product discount
    if (applicablePageDiscount === null) {
        const bestDiscount = selectLargestDiscount(productDiscounts, price)
        return bestDiscount === null ? bestDiscount : [bestDiscount]
    }

    // If the page discount can be combined with product discounts, find the
    // largest product discount that combines with the page discount's class.
    if (applicablePageDiscount.combinesWithProductDiscounts) {
        const combinableDiscounts = productDiscounts.filter(
            (discount) =>
                !promotionsMatch(discount, applicablePageDiscount) &&
                pageDiscountStacksWith(applicablePageDiscount, discount),
        )
        const bestDiscount = selectLargestDiscount(combinableDiscounts, price)
        if (bestDiscount !== null) {
            // Stacked discounts should be calculated using the product discount first and then
            // the page discount is applied on the discounted value.
            return [bestDiscount, applicablePageDiscount]
        }
    }

    // No combinable discounts are available, return the best discount of the page and product discounts
    const bestDiscount = selectLargestDiscount(
        [applicablePageDiscount, ...productDiscounts],
        price,
    )
    return bestDiscount === null ? bestDiscount : [bestDiscount]
}
